import { cancelSpeech, speakText, unlockSpeech } from "../bingo-shared/speech";
import { isHallDisplay } from "./role";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let mixGain: GainNode | null = null;
let mixSources: AudioScheduledSourceNode[] = [];
let mixClackTimer: number | null = null;
let brownBuf: AudioBuffer | null = null;

function audio(): AudioContext {
  if (!ctx) {
    ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)({
      latencyHint: "interactive",
    });
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
  }
  return ctx;
}

export function unlockHallAudio() {
  const ac = audio();
  if (ac.state === "suspended") void ac.resume();
  unlockSpeech();
}

export function resumeHallAudio() {
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();
}

function tone(
  freq: number,
  dur: number,
  type: OscillatorType,
  vol: number,
  slideTo?: number,
) {
  if (isHallDisplay()) return;
  const ac = audio();
  if (!master) return;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  const filter = ac.createBiquadFilter();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ac.currentTime);
  if (slideTo != null) osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), ac.currentTime + dur);
  filter.type = "lowpass";
  filter.frequency.value = Math.max(600, freq * 2.2);
  gain.gain.setValueAtTime(0.0001, ac.currentTime);
  gain.gain.exponentialRampToValueAtTime(vol, ac.currentTime + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  osc.start();
  osc.stop(ac.currentTime + dur + 0.02);
}

function brownNoise(ac: AudioContext) {
  if (brownBuf && brownBuf.sampleRate === ac.sampleRate) return brownBuf;
  const length = Math.floor(ac.sampleRate * 1.8);
  const buffer = ac.createBuffer(1, length, ac.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < length; i++) {
    last = (last + (Math.random() * 2 - 1) * 0.02) / 1.02;
    const sample = last * 3.6;
    data[i] = sample < -1 ? -1 : sample > 1 ? 1 : sample;
  }
  brownBuf = buffer;
  return buffer;
}

function mixClack(ac: AudioContext) {
  if (!master) return;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  const filter = ac.createBiquadFilter();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150 + Math.random() * 90, ac.currentTime);
  osc.frequency.exponentialRampToValueAtTime(70 + Math.random() * 30, ac.currentTime + 0.06);
  filter.type = "lowpass";
  filter.frequency.value = 520;
  filter.Q.value = 0.6;
  gain.gain.setValueAtTime(0.0001, ac.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.045 + Math.random() * 0.025, ac.currentTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.08);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  osc.start();
  osc.stop(ac.currentTime + 0.1);
}

function armClacks(ac: AudioContext) {
  const tick = () => {
    if (!mixGain) return;
    mixClack(ac);
    mixClackTimer = window.setTimeout(tick, 75 + Math.random() * 130);
  };
  mixClackTimer = window.setTimeout(tick, 50);
}

export function playMixRumble() {
  if (isHallDisplay()) return;
  stopMixRumble();
  const ac = audio();
  if (!master) return;

  const source = ac.createBufferSource();
  source.buffer = brownNoise(ac);
  source.loop = true;

  const hip = ac.createBiquadFilter();
  hip.type = "highpass";
  hip.frequency.value = 70;
  hip.Q.value = 0.5;

  const lop = ac.createBiquadFilter();
  lop.type = "lowpass";
  lop.frequency.value = 240;
  lop.Q.value = 0.85;

  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, ac.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.22, ac.currentTime + 0.14);

  const lfo = ac.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 2.4;
  const lfoDepth = ac.createGain();
  lfoDepth.gain.value = 70;
  lfo.connect(lfoDepth);
  lfoDepth.connect(lop.frequency);

  const pulse = ac.createOscillator();
  pulse.type = "sine";
  pulse.frequency.value = 5.6;
  const pulseDepth = ac.createGain();
  pulseDepth.gain.value = 0.045;
  pulse.connect(pulseDepth);
  pulseDepth.connect(gain.gain);

  source.connect(hip);
  hip.connect(lop);
  lop.connect(gain);
  gain.connect(master);

  source.start();
  lfo.start();
  pulse.start();

  mixGain = gain;
  mixSources = [source, lfo, pulse];
  armClacks(ac);
}

export function stopMixRumble() {
  if (mixClackTimer != null) {
    window.clearTimeout(mixClackTimer);
    mixClackTimer = null;
  }
  const sources = mixSources;
  const gain = mixGain;
  mixSources = [];
  mixGain = null;
  if (!gain) return;
  const ac = ctx;
  const halt = (ac?.currentTime ?? 0) + 0.18;
  if (ac) {
    gain.gain.cancelScheduledValues(ac.currentTime);
    gain.gain.setTargetAtTime(0.0001, ac.currentTime, 0.045);
  }
  for (const node of sources) {
    try {
      node.stop(halt);
    } catch {
      /* already stopped */
    }
  }
}

export function playDropWhoosh() {
  tone(180, 0.4, "sine", 0.16, 85);
}

export function playThud() {
  tone(90, 0.22, "sine", 0.45, 55);
  tone(180, 0.12, "triangle", 0.18);
}

export function playCallDing() {
  tone(784, 0.35, "sine", 0.28);
  tone(1175, 0.45, "triangle", 0.16);
}

export function playSeatClick() {
  tone(620, 0.16, "sine", 0.22, 420);
}

export function playVictory() {
  const notes = [523, 659, 784, 1046, 1319];
  notes.forEach((freq, i) => {
    window.setTimeout(() => tone(freq, 0.45, "sine", 0.32), i * 110);
  });
}

export function speakCall(text: string, repeats: number, pause = false) {
  if (isHallDisplay()) return;
  speakText(text, repeats, 1, pause);
}

export function hushHall() {
  stopMixRumble();
  cancelSpeech();
}
